import { useMemo } from "react";
import { useBuilderDesktop } from "../hooks/useBuilderDesktop";
import { createBlankDraft, getBuilderWarnings, readFormDefinition, serializeFormDefinition } from "@froppio/form-core";
import { FormBuilder } from "./FormBuilder";
import { FormPreview } from "./FormPreview";
import { UnsupportedDefinition } from "./UnsupportedDefinition";

interface FormDefinitionEditorProps {
  definition: string;
  onChange: (definition: string) => void;
}

export function FormDefinitionEditor({ definition, onChange }: FormDefinitionEditorProps) {
  const desktop = useBuilderDesktop();
  const result = useMemo(() => readFormDefinition(definition), [definition]);
  if (!result.success) return <UnsupportedDefinition definition={definition} message={result.message}
    onReset={desktop ? () => onChange(serializeFormDefinition(createBlankDraft())) : undefined} />;
  const warnings = getBuilderWarnings(result.draft);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">Form design</h2>
          <p className="text-xs text-muted-foreground">{desktop
            ? "Changes are saved only when you choose Save. Refreshing discards unsaved changes."
            : "Preview only on this screen size. Use a desktop browser to edit this form."}</p>
        </div>
      </div>
      {!desktop ? <FormPreview draft={result.draft} /> : (
        <>
          {warnings.length > 0 && <div role="status" className="rounded-xl border border-border bg-card p-4 text-sm">
            <p className="font-semibold">{warnings.length} design {warnings.length === 1 ? "warning" : "warnings"}</p>
            <p className="text-muted-foreground">You can save now and finish later. Preview explains what is missing.</p>
          </div>}
          <FormBuilder draft={result.draft} onChange={draft => onChange(serializeFormDefinition(draft))} />
        </>
      )}
    </div>
  );
}
